import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Chip,
  Paper,
  Divider,
  InputAdornment,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import { fNumber } from '../utils/formatNumber';

export const TENOR_MARGIN_DEFAULTS = {
  3: 5,   // 5% for 3 months
  6: 10,  // 10% for 6 months
  9: 12,  // 12% for 9 months
};

// Formats user input with comma separation while typing
const formatInputWithCommas = (val) => {
  if (val === undefined || val === null) return '';
  const clean = String(val).replace(/[^0-9.]/g, '');
  const parts = clean.split('.');
  const integerPart = parts[0] ? Number(parts[0]).toLocaleString('en-US') : '';
  if (parts.length > 1) {
    return `${integerPart}.${parts.slice(1).join('').slice(0, 2)}`;
  }
  return integerPart;
};

// Formats number to standard currency string on blur
const formatToCurrencyString = (val) => {
  if (val === undefined || val === null || String(val).trim() === '') return '';
  const clean = parseFloat(String(val).replace(/,/g, '')) || 0;
  if (clean === 0) return '';
  return fNumber(clean, 2);
};

export function BusinessProfitCalculatorContent({
  initialCost = '',
  initialTenor = '6',
  initialMarginRate = '',
  onApply,
  onClose,
}) {
  const theme = useTheme();

  const [costAmount, setCostAmount] = useState(() => formatInputWithCommas(initialCost ?? ''));
  const [tenor, setTenor] = useState(String(initialTenor || '6'));
  const [customMarginRate, setCustomMarginRate] = useState(
    initialMarginRate !== '' ? String(initialMarginRate) : String(TENOR_MARGIN_DEFAULTS[Number(initialTenor)] ?? 10)
  );

  const cleanCost = parseFloat(String(costAmount).replace(/,/g, '')) || 0;
  const tenorMonths = parseInt(tenor, 10) || 1;

  // When tenor changes, auto-set default profit margin if available, while still allowing custom override
  const handleTenorChange = (selectedMonths) => {
    setTenor(String(selectedMonths));
    const defaultRate = TENOR_MARGIN_DEFAULTS[selectedMonths];
    if (defaultRate !== undefined) {
      setCustomMarginRate(String(defaultRate));
    }
  };

  const marginRate = parseFloat(customMarginRate) || 0;
  const profitMarginAmount = Number(((cleanCost * marginRate) / 100).toFixed(2));
  const totalAmount = Number((cleanCost + profitMarginAmount).toFixed(2));
  const monthlyInstallment = tenorMonths > 0 ? Number((totalAmount / tenorMonths).toFixed(2)) : 0;

  const handleApply = () => {
    if (onApply) {
      onApply({
        costAmount: cleanCost,
        tenor: tenorMonths,
        profitMarginRate: marginRate,
        profitMarginAmount,
        totalAmount,
        monthlyInstallment,
      });
    }
    if (onClose) onClose();
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 540, mx: 'auto', p: { xs: 1.5, sm: 2 } }}>
      <Stack spacing={2.5}>
        {/* Cost of Item Purchased */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            1. Amount Used to Buy Item (Base Cost) *
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. 150,000.00"
            value={costAmount}
            onChange={(e) => setCostAmount(formatInputWithCommas(e.target.value))}
            onBlur={() => {
              if (costAmount) setCostAmount(formatToCurrencyString(costAmount));
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Typography sx={{ fontWeight: 800, color: 'primary.main' }}>₦</Typography></InputAdornment>,
              inputMode: 'decimal',
            }}
            helperText="Enter the purchase price paid to vendors/suppliers for the member"
          />
        </Box>

        {/* Tenor Selection with Auto-Margin Tags */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            2. Repayment Tenor & Standard Margin *
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { m: 3, rate: 5 },
              { m: 6, rate: 10 },
              { m: 9, rate: 12 },
            ].map(({ m, rate }) => {
              const isSelected = String(tenor) === String(m);
              return (
                <Button
                  key={m}
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => handleTenorChange(m)}
                  sx={{
                    flex: '1 1 120px',
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    bgcolor: isSelected ? '#0D9488' : 'background.paper',
                    color: isSelected ? '#FFFFFF' : '#0D9488',
                    borderColor: '#0D9488',
                    '&:hover': {
                      bgcolor: isSelected ? '#0F766E' : alpha('#0D9488', 0.08),
                    },
                  }}
                >
                  <Stack alignItems="center" spacing={0.2}>
                    <span>{m} Months</span>
                    <Chip
                      label={`+${rate}% Profit`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : alpha('#0D9488', 0.12),
                        color: isSelected ? '#FFFFFF' : '#0D9488',
                      }}
                    />
                  </Stack>
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Custom Tenor / Custom Margin Override */}
        <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Custom Tenor (Months)"
              size="small"
              type="number"
              value={tenor}
              onChange={(e) => setTenor(e.target.value)}
              inputProps={{ min: 1, max: 60 }}
              sx={{ flex: 1, width: '100%' }}
              helperText="E.g. 3, 6, 9 or custom"
            />
            <TextField
              label="Profit Margin %"
              size="small"
              type="number"
              value={customMarginRate}
              onChange={(e) => setCustomMarginRate(e.target.value)}
              inputProps={{ min: 0, step: 0.5 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{ flex: 1, width: '100%' }}
              helperText="Officer can customize margin %"
            />
          </Stack>
        </Box>

        {/* Live Calculation Results Breakdown Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: alpha('#059669', 0.06),
            border: `1.5px solid ${alpha('#059669', 0.25)}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <TrendingUpRoundedIcon sx={{ color: '#059669', fontSize: 22 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#059669' }}>
              Transaction & Profit Breakdown
            </Typography>
          </Stack>

          <Stack spacing={1.2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <ShoppingBagRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Base Cost of Goods:</Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                ₦{fNumber(cleanCost)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingUpRoundedIcon sx={{ fontSize: 18, color: '#059669' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Applied Profit Margin ({marginRate}% for {tenorMonths}m):
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#059669' }}>
                +₦{fNumber(profitMarginAmount)}
              </Typography>
            </Stack>

            <Divider sx={{ my: 0.5 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: 'text.primary' }}>
                Total Repayment Billed to Member:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#0B3B60' }}>
                ₦{fNumber(totalAmount)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <PaymentsRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Estimated Monthly Installment ({tenorMonths} months):
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#0D9488', fontSize: '0.9rem' }}>
                ₦{fNumber(monthlyInstallment)} / mo
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {/* Dedicated Account Notice */}
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#0B3B60', 0.05), border: `1px dashed ${alpha('#0B3B60', 0.2)}` }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.5 }}>
            🏛️ <strong>Dedicated Trade Profit Account:</strong> The ₦{fNumber(profitMarginAmount)} profit margin is automatically credited to the Society Trade Profit Ledger on the Business Officer Dashboard.
          </Typography>
        </Box>

        {/* Action Buttons */}
        {onApply && (
          <Button
            variant="contained"
            size="large"
            onClick={handleApply}
            disabled={cleanCost <= 0}
            startIcon={<CheckCircleRoundedIcon />}
            sx={{
              py: 1.25,
              fontWeight: 800,
              bgcolor: '#0D9488',
              '&:hover': { bgcolor: '#0F766E' },
            }}
          >
            Apply to Transaction (₦{fNumber(totalAmount)})
          </Button>
        )}
      </Stack>
    </Box>
  );
}

BusinessProfitCalculatorContent.propTypes = {
  initialCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialTenor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialMarginRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onApply: PropTypes.func,
  onClose: PropTypes.func,
};

export default function BusinessProfitCalculatorDialog({
  open,
  onClose,
  initialCost = '',
  initialTenor = '6',
  initialMarginRate = '',
  onApply,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1.5px solid ${alpha('#0D9488', 0.3)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 2,
          px: 3,
          bgcolor: alpha('#0D9488', 0.08),
          borderBottom: `1px solid ${alpha('#0D9488', 0.15)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: alpha('#0D9488', 0.15),
              color: '#0D9488',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalculateRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Business Profit & Tenor Calculator
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Standard: 5% (3m) • 10% (6m) • 12% (9m) with custom margin support
            </Typography>
          </Box>
        </Stack>

        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <BusinessProfitCalculatorContent
          initialCost={initialCost}
          initialTenor={initialTenor}
          initialMarginRate={initialMarginRate}
          onApply={onApply}
          onClose={onClose}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="text" sx={{ fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BusinessProfitCalculatorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialTenor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialMarginRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onApply: PropTypes.func,
};
